import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Button,
  LinearProgress, Divider, Avatar, Grow, Alert, AlertTitle
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import { jsPDF } from 'jspdf';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PieChartIcon from '@mui/icons-material/PieChart';
import HubIcon from '@mui/icons-material/Hub';
import PsychologyIcon from '@mui/icons-material/Psychology';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

// ── jsPDF helper: wrap text inside a rect ──
function drawWrappedText(pdf, text, x, y, maxWidth, lineHeight, maxLines) {
  const lines = pdf.splitTextToSize(text, maxWidth);
  const limited = maxLines ? lines.slice(0, maxLines) : lines;
  pdf.text(limited, x, y);
  return y + limited.length * lineHeight;
}

function generatePdf(metrics, playbook, quoteIds) {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = 210;
  const margin = 16;
  const contentW = pageW - margin * 2;
  let y = margin;

  const fmtNum = (n) => n != null ? n.toLocaleString('en-US') : '0';
  const fmtCur = (v) => {
    if (v == null) return '$0';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v);
  };

  // ── Header ──
  pdf.setFillColor(30, 60, 114);
  pdf.rect(0, 0, pageW, 32, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(18);
  pdf.text('AI Strategic Plan', margin, 16);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Historical performance analysis  •  ${quoteIds.length} quote${quoteIds.length !== 1 ? 's' : ''} analyzed`, margin, 24);
  y = 40;

  // ── Section helper ──
  const sectionTitle = (title) => {
    pdf.setFillColor(248, 250, 252);
    pdf.rect(margin, y - 4, contentW, 8, 'F');
    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.3);
    pdf.line(margin, y + 4, pageW - margin, y + 4);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(30, 60, 114);
    pdf.text(title, margin, y + 1.5);
    y += 12;
  };

  // ════════ METRICS TABLE ════════
  sectionTitle('Performance Metrics');
  const metricRows = [
    ['Total Deals', fmtNum(metrics.total_deals), 'Win Rate', String(metrics.win_pct)],
    ['Won', fmtNum(metrics.win_count), 'Lost', fmtNum(metrics.loss_count)],
    ['Avg Won Value', fmtCur(metrics.avg_win_value), 'Avg Lost Value', fmtCur(metrics.avg_loss_value)],
    ['Max Won Value', fmtCur(metrics.max_win_value), 'Max Lost Value', fmtCur(metrics.max_loss_value)],
    ['Min Won Value', fmtCur(metrics.min_win_value), '', ''],
  ];

  const tableStartY = y;
  const colW = contentW / 4;
  pdf.setFontSize(9);
  metricRows.forEach((row, ri) => {
    const rowY = tableStartY + ri * 9;
    if (ri % 2 === 0) {
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin, rowY - 3.5, contentW, 9, 'F');
    }
    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.1);
    pdf.rect(margin, rowY - 3.5, contentW, 9);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(100, 116, 139);
    pdf.text(row[0], margin + 2, rowY + 1);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(30, 41, 59);
    pdf.text(row[1], margin + colW - 2, rowY + 1, { align: 'right' });
    if (row[2]) {
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(100, 116, 139);
      pdf.text(row[2], margin + colW + 4, rowY + 1);
      pdf.setFont('helvetica', 'normal');
      if (row[2] === 'Win Rate') {
        pdf.setTextColor(16, 185, 129);
      } else if (row[2].includes('Lost')) {
        pdf.setTextColor(239, 68, 68);
      } else {
        pdf.setTextColor(30, 41, 59);
      }
      pdf.text(row[3], margin + colW * 3 - 2, rowY + 1, { align: 'right' });
    }
  });
  y = tableStartY + metricRows.length * 9 + 6;

  // Account frequency notes
  if (metrics.account_frequency_notes) {
    pdf.setFillColor(248, 250, 252);
    pdf.rect(margin, y - 2, contentW, 14, 'F');
    pdf.setDrawColor(226, 232, 240);
    pdf.rect(margin, y - 2, contentW, 14);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(71, 85, 105);
    pdf.text('Account Insight:', margin + 2, y + 2);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 116, 139);
    const notesLines = pdf.splitTextToSize(metrics.account_frequency_notes, contentW - 10);
    pdf.text(notesLines, margin + 2, y + 6, { maxWidth: contentW - 10 });
    y += 10 + notesLines.length * 4;
  }

  y += 6;

  // ════════ EXECUTIVE SUMMARY ════════
  if (playbook && playbook.executive_summary) {
    sectionTitle('AI Executive Summary');
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(51, 65, 85);
    y = drawWrappedText(pdf, playbook.executive_summary, margin, y, contentW, 5, 30);
    y += 4;
  }

  // ════════ WINNING PLAYS ════════
  if (playbook && playbook.winning_streaks && playbook.winning_streaks.length > 0) {
    sectionTitle('Winning Plays');
    playbook.winning_streaks.forEach((streak) => {
      // Title
      pdf.setFillColor(240, 253, 244);
      const titleH = 7;
      pdf.rect(margin, y - 3.5, contentW, titleH, 'F');
      pdf.setDrawColor(16, 185, 129);
      pdf.setLineWidth(0.8);
      pdf.line(margin, y - 3.5, margin, y + titleH - 3.5);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(16, 185, 129);
      pdf.text(streak.title, margin + 3, y + 0.5);
      y += titleH + 1;

      // Proof
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(5, 150, 105);
      pdf.text('Proof:', margin + 2, y + 1);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(51, 65, 85);
      y = drawWrappedText(pdf, streak.metric_proof, margin + 17, y + 0.5, contentW - 19, 4, 8);
      y += 2;

      // Strategic Action
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(59, 130, 246);
      pdf.text('Strategic Action:', margin + 2, y + 1);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(51, 65, 85);
      y = drawWrappedText(pdf, streak.strategic_takeaway, margin + 27, y + 0.5, contentW - 29, 4, 8);
      y += 4;
    });
  }

  // ════════ LOSING TRAPS ════════
  if (playbook && playbook.losing_traps && playbook.losing_traps.length > 0) {
    sectionTitle('Losing Traps');
    playbook.losing_traps.forEach((trap) => {
      // Title
      pdf.setFillColor(254, 242, 242);
      const titleH2 = 7;
      pdf.rect(margin, y - 3.5, contentW, titleH2, 'F');
      pdf.setDrawColor(239, 68, 68);
      pdf.setLineWidth(0.8);
      pdf.line(margin, y - 3.5, margin, y + titleH2 - 3.5);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(239, 68, 68);
      pdf.text(trap.title, margin + 3, y + 0.5);
      y += titleH2 + 1;

      // Proof
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(220, 38, 38);
      pdf.text('Proof:', margin + 2, y + 1);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(51, 65, 85);
      y = drawWrappedText(pdf, trap.metric_proof, margin + 17, y + 0.5, contentW - 19, 4, 8);
      y += 2;

      // Mitigation
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(217, 119, 6);
      pdf.text('Mitigation:', margin + 2, y + 1);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(51, 65, 85);
      y = drawWrappedText(pdf, trap.strategic_takeaway, margin + 27, y + 0.5, contentW - 29, 4, 8);
      y += 4;
    });
  }

  // ── Generate charts as images and embed ──
  // We'll capture from the existing canvases
  const canvasDoughnut = document.querySelector('canvas');
  const canvasBar = document.querySelectorAll('canvas')[1];

  if (canvasDoughnut && canvasBar) {
    // Check if we need a new page
    if (y > 210) {
      pdf.addPage();
      y = margin;
    }
    y += 4;
    sectionTitle('Visual Analysis');

    const chartW = contentW / 2 - 4;
    const chartH = chartW;

    const doughImg = canvasDoughnut.toDataURL('image/png');
    pdf.addImage(doughImg, 'PNG', margin, y, chartW, chartH);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(30, 41, 59);
    pdf.text('Win / Loss Distribution', margin + chartW / 2, y + chartH + 4, { align: 'center' });

    const barImg = canvasBar.toDataURL('image/png');
    pdf.addImage(barImg, 'PNG', margin + chartW + 8, y, chartW, chartH);
    pdf.text('Deal Value Comparison', margin + chartW + 8 + chartW / 2, y + chartH + 4, { align: 'center' });
  }

  // ── Footer ──
  const pageCount = pdf.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(7);
    pdf.setTextColor(148, 163, 184);
    pdf.text(`AI Strategic Plan  •  Generated ${new Date().toLocaleDateString()}  •  Page ${i} of ${pageCount}`, margin, 292);
  }

  pdf.save(`AI_Strategic_Plan_${new Date().toISOString().split('T')[0]}.pdf`);
}

// ────────────────────────────────────────────────────────────────────
function StrategicPlan() {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const quoteIds = (location.state && location.state.quoteIds) || [];

  const fetchStrategicPlan = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API_URL}/ai-reports/historical-summary`,
        { quote_ids: quoteIds },
        { headers }
      );
      setData(res.data);
    } catch (err) {
      console.error('Strategic Plan fetch error:', err);
      setError(err.response?.data?.message || 'Failed to load strategic plan data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [quoteIds]);

  useEffect(() => {
    fetchStrategicPlan();
  }, [fetchStrategicPlan]);

  const formatCurrency = (amount) => {
    if (amount == null) return '$0';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
  };

  const handleExportPdf = () => {
    if (exportBtnRef.current) exportBtnRef.current.style.display = 'none';
    generatePdf(data.metrics, data.ai_playbook, quoteIds);
    setTimeout(() => {
      if (exportBtnRef.current) exportBtnRef.current.style.display = '';
    }, 500);
  };

  const exportBtnRef = useRef(null);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', gap: 4, bgcolor: '#f4f6f8' }}>
        <Box sx={{
          width: 100, height: 100, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)'
        }}>
          <PsychologyIcon sx={{ fontSize: 52, color: '#90caf9' }} />
        </Box>
        <Typography variant="h5" fontWeight="700" color="#1e293b">Analyzing Quote Performance...</Typography>
        <Typography variant="body1" color="#64748b">Crunching historical data to generate your AI-powered strategic plan</Typography>
        <Box sx={{ width: 400, maxWidth: '80%' }}>
          <LinearProgress sx={{ borderRadius: 2, height: 6, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#3b82f6' } }} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, maxWidth: 900, mx: 'auto', mt: 8 }}>
        <Alert severity="error" sx={{ borderRadius: 3, boxShadow: '0 8px 30px rgba(239,68,68,0.1)' }}>
          <AlertTitle sx={{ fontWeight: 700 }}>Error Loading Strategic Plan</AlertTitle>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
          sx={{ mt: 3, borderRadius: 2, fontWeight: 'bold', color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!data || !data.metrics) {
    return (
      <Box sx={{ p: 4, maxWidth: 900, mx: 'auto', mt: 8, textAlign: 'center' }}>
        <WarningAmberIcon sx={{ fontSize: 64, color: '#f59e0b', mb: 2 }} />
        <Typography variant="h5" fontWeight="700" color="#1e293b">No Strategic Plan Data Available</Typography>
        <Typography color="#64748b" mt={1}>Try selecting a different set of quotes with historical activity.</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
          sx={{ mt: 3, borderRadius: 2, fontWeight: 'bold', color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }}>
          Go Back
        </Button>
      </Box>
    );
  }

  const metrics = data.metrics;
  const playbook = data.ai_playbook;

  // PM-compatible metric card style
  const metricCardSx = (highlightColor) => ({
    borderRadius: 4,
    border: '1px solid #f1f5f9',
    textAlign: 'center',
    p: 2.5,
    bgcolor: 'white',
    boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
    transition: 'all 0.3s',
    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 28px rgba(0,0,0,0.08)', borderColor: '#cbd5e1' }
  });

  return (
    <Box sx={{ width: '100%', bgcolor: '#f4f6f8', minHeight: '100vh', pb: 8 }}>
      {/* PM-Compatible Blue Gradient Header */}
      <Grow in={true} timeout={500}>
        <Box sx={{
          mb: 4, mt: 2, mx: { xs: 1, md: 3 }, p: 4, borderRadius: 4,
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          color: 'white',
          boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
          position: 'relative', overflow: 'hidden',
          display: 'flex', flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 3
        }}>
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
              <PsychologyIcon sx={{ fontSize: 40, color: '#90caf9' }} />
              AI Strategic Plan
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              Historical performance analysis & AI-generated playbook for selected quotes
            </Typography>
            {quoteIds.length > 0 && (
              <Chip label={`${quoteIds.length} quote${quoteIds.length > 1 ? 's' : ''} analyzed`} size="small"
                sx={{ mt: 1.5, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600, borderRadius: 1.5 }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, position: 'relative', zIndex: 2 }}>
            <Button ref={exportBtnRef} variant="contained" startIcon={<PictureAsPdfIcon />} onClick={handleExportPdf}
              sx={{
                borderRadius: 2, fontWeight: 'bold', px: 3, py: 1,
                bgcolor: 'white', color: '#1e3c72',
                boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                '&:hover': { bgcolor: '#f0f0f0', transform: 'translateY(-2px)' },
                transition: 'all 0.2s'
              }}>
              Export PDF
            </Button>
            <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2, fontWeight: 'bold', px: 3, py: 1,
                bgcolor: 'rgba(255,255,255,0.2)', color: 'white', boxShadow: 'none',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
              }}>
              Back to Quotes
            </Button>
          </Box>
          <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
          <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        </Box>
      </Grow>

      <Box sx={{ px: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>

        {/* Key Metrics Cards */}
        <Grow in={true} timeout={700}>
          <Box sx={{ mb: 5 }}>
            <Typography variant="h6" fontWeight="800" color="#1e293b" mb={2.5} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QueryStatsIcon sx={{ color: '#3b82f6' }} /> Performance Metrics
            </Typography>
            <Grid container spacing={2.5}>
              <Grid item xs={6} sm={4} md={3}>
                <Card elevation={0} sx={metricCardSx()}>
                  <AccountBalanceWalletIcon sx={{ fontSize: 32, color: '#3b82f6', mb: 1 }} />
                  <Typography variant="h4" fontWeight="800" color="#1e293b">{metrics.total_deals}</Typography>
                  <Typography variant="caption" fontWeight="600" color="#64748b" textTransform="uppercase" letterSpacing="0.5px">Total Deals</Typography>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Card elevation={0} sx={metricCardSx()}>
                  <PieChartIcon sx={{ fontSize: 32, color: '#10b981', mb: 1 }} />
                  <Typography variant="h4" fontWeight="800" color="#10b981">{metrics.win_pct}</Typography>
                  <Typography variant="caption" fontWeight="600" color="#64748b" textTransform="uppercase" letterSpacing="0.5px">Win Rate</Typography>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Card elevation={0} sx={metricCardSx()}>
                  <TrendingUpIcon sx={{ fontSize: 32, color: '#10b981', mb: 1 }} />
                  <Typography variant="h5" fontWeight="800" color="#10b981">{metrics.win_count}</Typography>
                  <Typography variant="caption" fontWeight="600" color="#64748b" textTransform="uppercase" letterSpacing="0.5px">Won</Typography>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Card elevation={0} sx={metricCardSx()}>
                  <TrendingDownIcon sx={{ fontSize: 32, color: '#ef4444', mb: 1 }} />
                  <Typography variant="h5" fontWeight="800" color="#ef4444">{metrics.loss_count}</Typography>
                  <Typography variant="caption" fontWeight="600" color="#64748b" textTransform="uppercase" letterSpacing="0.5px">Lost</Typography>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Card elevation={0} sx={metricCardSx()}>
                  <AutoGraphIcon sx={{ fontSize: 32, color: '#10b981', mb: 1 }} />
                  <Typography variant="h6" fontWeight="800" color="#10b981">{formatCurrency(metrics.avg_win_value)}</Typography>
                  <Typography variant="caption" fontWeight="600" color="#64748b" textTransform="uppercase" letterSpacing="0.5px">Avg Won Value</Typography>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Card elevation={0} sx={metricCardSx()}>
                  <AutoGraphIcon sx={{ fontSize: 32, color: '#ef4444', mb: 1 }} />
                  <Typography variant="h6" fontWeight="800" color="#ef4444">{formatCurrency(metrics.avg_loss_value)}</Typography>
                  <Typography variant="caption" fontWeight="600" color="#64748b" textTransform="uppercase" letterSpacing="0.5px">Avg Lost Value</Typography>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Card elevation={0} sx={metricCardSx()}>
                  <EmojiEventsIcon sx={{ fontSize: 32, color: '#f59e0b', mb: 1 }} />
                  <Typography variant="h6" fontWeight="800" color="#f59e0b">{formatCurrency(metrics.max_win_value)}</Typography>
                  <Typography variant="caption" fontWeight="600" color="#64748b" textTransform="uppercase" letterSpacing="0.5px">Max Win Value</Typography>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Card elevation={0} sx={metricCardSx()}>
                  <WarningAmberIcon sx={{ fontSize: 32, color: '#ef4444', mb: 1 }} />
                  <Typography variant="h6" fontWeight="800" color="#ef4444">{formatCurrency(metrics.max_loss_value)}</Typography>
                  <Typography variant="caption" fontWeight="600" color="#64748b" textTransform="uppercase" letterSpacing="0.5px">Max Loss Value</Typography>
                </Card>
              </Grid>
            </Grid>
            {metrics.account_frequency_notes && (
              <Card elevation={0} sx={{ mt: 2.5, borderRadius: 4, border: '1px solid #f1f5f9', bgcolor: '#f8fafc', p: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                <Typography variant="body2" fontWeight="600" color="#475569" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HubIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                  Account Insight:
                  <Typography component="span" variant="body2" fontWeight="400" color="#64748b">{metrics.account_frequency_notes}</Typography>
                </Typography>
              </Card>
            )}
          </Box>
        </Grow>

        {/* Graphs Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight="800" color="#1e293b" mb={2.5} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PieChartIcon sx={{ color: '#3b82f6' }} /> Visual Analysis
          </Typography>
          <Grid container spacing={3}>
            {/* Win/Loss Doughnut Chart */}
            <Grid item xs={12} md={6}>
              <Card elevation={0} sx={{
                borderRadius: 4, border: '1px solid #f1f5f9',
                boxShadow: '0 4px 20px rgba(0,0,0,0.03)', bgcolor: 'white', height: '100%',
                transition: 'all 0.3s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 28px rgba(0,0,0,0.08)', borderColor: '#cbd5e1' }
              }}>
                <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography variant="subtitle1" fontWeight="700" color="#1e293b" mb={2}>Win / Loss Distribution</Typography>
                  <Box sx={{ width: '100%', maxWidth: 280, height: 280, position: 'relative' }}>
                    <Doughnut
                      data={{
                        labels: ['Won', 'Lost'],
                        datasets: [{
                          data: [metrics.win_count || 0, metrics.loss_count || 0],
                          backgroundColor: ['#10b981', '#ef4444'],
                          borderColor: ['#059669', '#dc2626'],
                          borderWidth: 2,
                          hoverBorderColor: ['#047857', '#b91c1c'],
                          hoverBorderWidth: 3,
                        }],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: true,
                        cutout: '65%',
                        animation: false,
                        plugins: {
                          legend: {
                            position: 'bottom',
                            labels: {
                              padding: 20,
                              usePointStyle: true,
                              pointStyleWidth: 10,
                              font: { size: 13, weight: '600' },
                              color: '#475569',
                            }
                          },
                          tooltip: {
                            backgroundColor: 'rgba(30,41,59,0.95)',
                            titleFont: { weight: '700', size: 14 },
                            bodyFont: { size: 13 },
                            padding: 12,
                            cornerRadius: 8,
                            callbacks: {
                              label: function(ctx) {
                                const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                                const pct = total > 0 ? Math.round((ctx.raw / total) * 100) : 0;
                                return ` ${ctx.label}: ${ctx.raw} (${pct}%)`;
                              }
                            }
                          }
                        }
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Value Comparison Bar Chart */}
            <Grid item xs={12} md={6}>
              <Card elevation={0} sx={{
                borderRadius: 4, border: '1px solid #f1f5f9',
                boxShadow: '0 4px 20px rgba(0,0,0,0.03)', bgcolor: 'white', height: '100%',
                transition: 'all 0.3s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 28px rgba(0,0,0,0.08)', borderColor: '#cbd5e1' }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="subtitle1" fontWeight="700" color="#1e293b" mb={2}>Deal Value Comparison</Typography>
                  <Box sx={{ width: '100%', height: 280 }}>
                    <Bar
                      data={{
                        labels: ['Avg Win', 'Avg Loss', 'Max Win', 'Max Loss'],
                        datasets: [{
                          label: 'Deal Value (USD)',
                          data: [
                            metrics.avg_win_value || 0,
                            metrics.avg_loss_value || 0,
                            metrics.max_win_value || 0,
                            metrics.max_loss_value || 0,
                          ],
                          backgroundColor: ['#10b981', '#ef4444', '#059669', '#dc2626'],
                          borderRadius: 10,
                          borderSkipped: false,
                          barThickness: 48,
                        }],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        animation: false,
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                            backgroundColor: 'rgba(30,41,59,0.95)',
                            titleFont: { weight: '700', size: 14 },
                            bodyFont: { size: 13 },
                            padding: 12,
                            cornerRadius: 8,
                            callbacks: {
                              label: function(ctx) {
                                return ` ${formatCurrency(ctx.raw)}`;
                              }
                            }
                          }
                        },
                        scales: {
                          x: {
                            grid: { display: false },
                            ticks: { color: '#64748b', font: { weight: '600', size: 12 } },
                          },
                          y: {
                            beginAtZero: true,
                            grid: { color: '#f1f5f9' },
                            ticks: {
                              color: '#94a3b8',
                              font: { size: 11 },
                              callback: function(value) {
                                if (value >= 1000000) return '$' + (value / 1000000).toFixed(1) + 'M';
                                if (value >= 1000) return '$' + (value / 1000).toFixed(0) + 'K';
                                return '$' + value;
                              }
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Executive Summary */}
        {playbook && playbook.executive_summary && (
          <Grow in={true} timeout={900}>
            <Card elevation={0} sx={{
              mb: 4, borderRadius: 4, border: '1px solid #f1f5f9', overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)'
            }}>
              <Box sx={{
                p: 3, background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: 'white',
                display: 'flex', alignItems: 'center', gap: 1.5
              }}>
                <PsychologyIcon sx={{ fontSize: 28, color: '#90caf9' }} />
                <Typography color="inherit" variant="h6" fontWeight="800">AI Executive Summary</Typography>
              </Box>
              <CardContent sx={{ p: 3.5 }}>
                <Typography variant="body1" color="#334155" sx={{ lineHeight: 1.9, fontSize: '0.95rem' }}>
                  {playbook.executive_summary}
                </Typography>
              </CardContent>
            </Card>
          </Grow>
        )}

        {/* Winning Streaks & Losing Traps */}
        <Grid container spacing={4}>
          {/* Winning Streaks */}
          <Grid item xs={12} md={6}>
            <Grow in={true} timeout={1100}>
              <Card elevation={0} sx={{
                height: '100%', borderRadius: 4, border: '1px solid #f1f5f9',
                boxShadow: '0 4px 20px rgba(0,0,0,0.03)', overflow: 'hidden'
              }}>
                <Box sx={{
                  p: 2.5, background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)', color: 'white',
                  display: 'flex', alignItems: 'center', gap: 1.5
                }}>
                  <MilitaryTechIcon sx={{ fontSize: 24 }} />
                  <Typography color="inherit" variant="h6" fontWeight="800">Winning Plays</Typography>
                  <Chip label={`${(playbook?.winning_streaks || []).length} strategies`} size="small"
                    sx={{ ml: 'auto', bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 600, borderRadius: 1.5 }} />
                </Box>
                <CardContent sx={{ p: 2.5 }}>
                  {(playbook?.winning_streaks || []).map((streak, idx) => (
                    <Box key={idx} sx={{ mb: idx < (playbook?.winning_streaks || []).length - 1 ? 2.5 : 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: '#10b981', width: 32, height: 32, fontSize: '0.8rem', fontWeight: 800, flexShrink: 0 }}>
                          <LightbulbIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="700" color="#1e293b">{streak.title}</Typography>
                          <Box sx={{ bgcolor: '#f0fdf4', borderRadius: 2, p: 1.2, mt: 0.8, borderLeft: '3px solid #10b981' }}>
                            <Typography variant="caption" fontWeight="700" color="#059669" display="block">Proof</Typography>
                            <Typography variant="body2" color="#334155" sx={{ fontSize: '0.82rem' }}>{streak.metric_proof}</Typography>
                          </Box>
                          <Box sx={{ bgcolor: '#eff6ff', borderRadius: 2, p: 1.2, mt: 1, borderLeft: '3px solid #3b82f6' }}>
                            <Typography variant="caption" fontWeight="700" color="#3b82f6" display="block">Strategic Action</Typography>
                            <Typography variant="body2" color="#334155" sx={{ fontSize: '0.82rem' }}>{streak.strategic_takeaway}</Typography>
                          </Box>
                        </Box>
                      </Box>
                      {idx < (playbook?.winning_streaks || []).length - 1 && <Divider sx={{ mt: 2.5, borderColor: '#f1f5f9' }} />}
                    </Box>
                  ))}
                  {(!playbook?.winning_streaks || playbook.winning_streaks.length === 0) && (
                    <Typography variant="body2" color="#94a3b8" textAlign="center" py={3}>No winning streaks identified for this selection.</Typography>
                  )}
                </CardContent>
              </Card>
            </Grow>
          </Grid>

          {/* Losing Traps */}
          <Grid item xs={12} md={6}>
            <Grow in={true} timeout={1300}>
              <Card elevation={0} sx={{
                height: '100%', borderRadius: 4, border: '1px solid #f1f5f9',
                boxShadow: '0 4px 20px rgba(0,0,0,0.03)', overflow: 'hidden'
              }}>
                <Box sx={{
                  p: 2.5, background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)', color: 'white',
                  display: 'flex', alignItems: 'center', gap: 1.5
                }}>
                  <WarningAmberIcon sx={{ fontSize: 24 }} />
                  <Typography color="inherit" variant="h6" fontWeight="800">Losing Traps</Typography>
                  <Chip label={`${(playbook?.losing_traps || []).length} traps`} size="small"
                    sx={{ ml: 'auto', bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 600, borderRadius: 1.5 }} />
                </Box>
                <CardContent sx={{ p: 2.5 }}>
                  {(playbook?.losing_traps || []).map((trap, idx) => (
                    <Box key={idx} sx={{ mb: idx < (playbook?.losing_traps || []).length - 1 ? 2.5 : 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: '#ef4444', width: 32, height: 32, fontSize: '0.8rem', fontWeight: 800, flexShrink: 0 }}>
                          <WarningAmberIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Box sx={{ width: '100%' }}>
                          <Typography variant="subtitle1" fontWeight="700" color="#1e293b">{trap.title}</Typography>
                          <Box sx={{ bgcolor: '#fef2f2', borderRadius: 2, p: 1.2, mt: 0.8, borderLeft: '3px solid #ef4444' }}>
                            <Typography variant="caption" fontWeight="700" color="#dc2626" display="block">Proof</Typography>
                            <Typography variant="body2" color="#334155" sx={{ fontSize: '0.82rem' }}>{trap.metric_proof}</Typography>
                          </Box>
                          <Box sx={{ bgcolor: '#fffbeb', borderRadius: 2, p: 1.2, mt: 1, borderLeft: '3px solid #f59e0b' }}>
                            <Typography variant="caption" fontWeight="700" color="#d97706" display="block">Mitigation</Typography>
                            <Typography variant="body2" color="#334155" sx={{ fontSize: '0.82rem' }}>{trap.strategic_takeaway}</Typography>
                          </Box>
                        </Box>
                      </Box>
                      {idx < (playbook?.losing_traps || []).length - 1 && <Divider sx={{ mt: 2.5, borderColor: '#f1f5f9' }} />}
                    </Box>
                  ))}
                  {(!playbook?.losing_traps || playbook.losing_traps.length === 0) && (
                    <Typography variant="body2" color="#94a3b8" textAlign="center" py={3}>No losing traps identified for this selection.</Typography>
                  )}
                </CardContent>
              </Card>
            </Grow>
          </Grid>
        </Grid>

        {/* Refresh button footer */}
        <Box sx={{ textAlign: 'center', mt: 5 }}>
          <Button variant="contained" startIcon={<AutoGraphIcon />} onClick={fetchStrategicPlan}
            sx={{
              borderRadius: 2, fontWeight: 'bold', px: 4, py: 1.2,
              bgcolor: '#3b82f6', color: 'white',
              boxShadow: '0 4px 14px rgba(59,130,246,0.3)',
              '&:hover': { bgcolor: '#2563eb', transform: 'translateY(-2px)' },
              transition: 'all 0.2s'
            }}>
            Refresh Analysis
          </Button>
        </Box>

      </Box>
    </Box>
  );
}

export default StrategicPlan;